import React from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Shield,
  UserCog,
  Eye,
  Edit,
  CheckCircle2,
  XCircle,
} from 'lucide-react';

const Roles = () => {
  // Define the system roles with their descriptions and permissions
  const systemRoles = [
    {
      id: 'admin',
      name: 'Administrador',
      description: 'Acceso total al sistema. Puede gestionar usuarios, equipos, KPIs, metas y configuración.',
      color: 'bg-red-100 text-red-700',
      permissions: [
        { name: 'Gestionar usuarios', allowed: true },
        { name: 'Gestionar equipos', allowed: true },
        { name: 'Crear/editar KPIs', allowed: true },
        { name: 'Configurar metas', allowed: true },
        { name: 'Capturar resultados', allowed: true },
        { name: 'Ver dashboard y análisis', allowed: true },
        { name: 'Configurar calendario', allowed: true },
        { name: 'Editar periodos cerrados', allowed: true },
        { name: 'Ver auditoría', allowed: true },
      ],
    },
    {
      id: 'user',
      name: 'Operador',
      description: 'Usuario estándar que puede capturar resultados de los KPIs asignados a sus equipos.',
      color: 'bg-blue-100 text-blue-700',
      permissions: [
        { name: 'Gestionar usuarios', allowed: false },
        { name: 'Gestionar equipos', allowed: false },
        { name: 'Crear/editar KPIs', allowed: false },
        { name: 'Configurar metas', allowed: false },
        { name: 'Capturar resultados', allowed: true },
        { name: 'Ver dashboard y análisis', allowed: true },
        { name: 'Configurar calendario', allowed: false },
        { name: 'Editar periodos cerrados', allowed: false },
        { name: 'Ver auditoría', allowed: false },
      ],
    },
    {
      id: 'viewer',
      name: 'Viewer',
      description: 'Acceso de solo lectura. Puede visualizar el dashboard y los KPIs asignados sin poder modificar nada.',
      color: 'bg-slate-100 text-slate-700',
      permissions: [
        { name: 'Gestionar usuarios', allowed: false },
        { name: 'Gestionar equipos', allowed: false },
        { name: 'Crear/editar KPIs', allowed: false },
        { name: 'Configurar metas', allowed: false },
        { name: 'Capturar resultados', allowed: false },
        { name: 'Ver dashboard y análisis', allowed: true },
        { name: 'Configurar calendario', allowed: false },
        { name: 'Editar periodos cerrados', allowed: false },
        { name: 'Ver auditoría', allowed: false },
      ],
    },
  ];

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Manrope']">
            Roles del Sistema
          </h1>
          <p className="text-slate-500 mt-1">
            Consulta los roles disponibles y sus permisos asociados
          </p>
        </div>

        <div className="grid gap-6">
          {systemRoles.map((role) => (
            <Card key={role.id} data-testid={`role-card-${role.id}`}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${role.color}`}>
                      {role.id === 'admin' ? (
                        <Shield className="w-5 h-5" />
                      ) : role.id === 'user' ? (
                        <UserCog className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </div>
                    <div>
                      <CardTitle className="text-lg">{role.name}</CardTitle>
                      <CardDescription className="mt-1">{role.description}</CardDescription>
                    </div>
                  </div>
                  <Badge className={role.color}>{role.id.toUpperCase()}</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <h4 className="text-sm font-medium text-slate-700 mb-3">Permisos</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {role.permissions.map((perm, idx) => (
                    <div
                      key={idx}
                      className={`flex items-center gap-2 p-2 rounded-lg text-sm ${
                        perm.allowed ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-50 text-slate-400'
                      }`}
                    >
                      {perm.allowed ? (
                        <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                      ) : (
                        <XCircle className="w-4 h-4 flex-shrink-0" />
                      )}
                      <span>{perm.name}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Info Card */}
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <Shield className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-amber-900">Roles predefinidos</h4>
                <p className="text-sm text-amber-700 mt-1">
                  Los roles del sistema están predefinidos y no pueden ser modificados. 
                  Para cambiar los permisos de un usuario, asígnale un rol diferente desde la sección de Usuarios.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Roles;
